import { memo, useMemo } from 'react'
import { HistoryRow, HistoryRowIcon } from '../../../components/history/history-row'
import type { ViewOpenTarget } from '@renderer/overlay/Sidepanel/view-atoms'
import { HistorySelectCheckbox } from './history-select-checkbox'
import type { RecentActivity } from '@renderer/overlay/Sidepanel/types'

interface HistorySelectableRowProps {
  item: RecentActivity
  onOpen: (item: RecentActivity, open: ViewOpenTarget) => void
  /** Row index in the flat filtered list, for shift-range selection. */
  selectIndex: number
}

/**
 * The shared `HistoryRow` with this view's selection checkbox in its leading
 * slot. Selection lives here rather than in the row itself so Home's Recents
 * preview can render the same row without mounting `HistorySelectProvider`
 * (whose context hook throws when absent) or the bulk-delete machinery.
 *
 * The `leading` element is memoized because `HistoryRow` is memoized — a fresh
 * element each render would defeat both its bail-out and the checkbox's.
 */
export const HistorySelectableRow = memo(function HistorySelectableRow({
  item,
  onOpen,
  selectIndex,
}: HistorySelectableRowProps) {
  const leading = useMemo(
    () => (
      <HistorySelectCheckbox
        item={{ id: item.id, type: item.type }}
        icon={<HistoryRowIcon item={item} />}
        index={selectIndex}
      />
    ),
    [item, selectIndex],
  )

  return <HistoryRow item={item} onOpen={onOpen} leading={leading} />
})
